import { toast } from "sonner";
import { logoutApi } from "../Apis/login";
import { clearAllTimers } from "./services/tokenRefreshService";
import { store, persistor } from "../redux/store";
import { clearTokens } from "../redux/slices/AuthSlice";
import { clearUser } from "../redux/slices/UserSlice";
import { clearLoginMeta } from "../redux/slices/LoginBetaSlice";
import { clearForgotPasswordData } from "../redux/slices/ForgotPasswordSlice";
import { clearPasswordSetup } from "../redux/slices/ResetPassword";
import { clearOpportunities } from "../redux/slices/OpportunitiesSlice";
import { clearUsers } from "../redux/slices/UsersListSlice";
import { clearDepartmentsList } from "../redux/slices/DepartmentsListSlice";
import { clearIntegrations } from "../redux/slices/GoogleAdsSlice";
import { clearGlobal } from "../redux/slices/GlobalSlice";
import { clearMyTasks } from "../redux/slices/MyTasksSlice";
import { clearNotifications } from "../redux/slices/NotificationsSlice";
import { baseApi } from "../redux/api/baseApi";
import { disconnectSocket } from "../services/socket";
import { resetAnalytics } from "../analytics";
import { clearSensitiveClientStorage } from "../utils/clearSensitiveClientStorage";
import { LOGOUT_STORAGE_KEY } from "../utils/sessionLogout";

export interface PerformLogoutOptions {
  /** Call POST /auth/logout before clearing local state. Default: true */
  callLogoutApi?: boolean;
  /** Broadcast logout to other browser tabs. Default: true */
  broadcastMultiTab?: boolean;
  /** Show a session-expired toast. */
  showToast?: boolean;
  toastMessage?: string;
}

let isLogoutInProgress = false;

export function resetLogoutInProgressForTests(): void {
  isLogoutInProgress = false;
}

/**
 * Centralized logout: API, timers, socket, Redux, persisted auth, and sensitive storage.
 */
export async function performLogout(
  options: PerformLogoutOptions = {}
): Promise<void> {
  if (isLogoutInProgress) {
    return;
  }

  const {
    callLogoutApi = true,
    broadcastMultiTab = true,
    showToast = false,
    toastMessage = "Session expired. Please login again.",
  } = options;

  isLogoutInProgress = true;

  try {
    clearAllTimers();
    disconnectSocket();

    try {
      resetAnalytics();
    } catch {
      // fail-open: analytics reset must never block logout teardown
    }

    if (callLogoutApi) {
      try {
        await logoutApi();
      } catch {
        // still clear local session if API fails
      }
    }

    store.dispatch(clearTokens());
    store.dispatch(clearUser());
    store.dispatch(clearLoginMeta());
    store.dispatch(clearForgotPasswordData());
    store.dispatch(clearPasswordSetup());
    store.dispatch(clearOpportunities());
    store.dispatch(clearUsers());
    store.dispatch(clearDepartmentsList());
    store.dispatch(clearIntegrations());
    store.dispatch(clearGlobal());
    store.dispatch(clearMyTasks());
    store.dispatch(clearNotifications());
    store.dispatch(baseApi.util.resetApiState());

    clearSensitiveClientStorage();

    try {
      await persistor.purge();
    } catch {
      // ignore purge errors
    }

    if (broadcastMultiTab && typeof window !== "undefined") {
      window.localStorage.setItem(LOGOUT_STORAGE_KEY, String(Date.now()));
    }

    if (showToast) {
      toast.error(toastMessage, { closeButton: true });
    }
  } finally {
    isLogoutInProgress = false;
  }
}
